import React, { useEffect, useState, MouseEvent, Dispatch, SetStateAction } from 'react';
import { useGlobalization } from '@Translations';
import { useGradeMatrixStyles } from '@Components/deteriorationModel/gradeSetupTab/GradeMatrixStyles';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	InputAdornment,
	ListItemIcon,
	Menu,
	MenuItem,
	Paper,
	Skeleton,
	TextField,
	Typography,
} from '@mui/material';
import { legends, legendsColors } from '@Components/deteriorationModel/gradeSetupTab/utils.GradeMatrix';
import StarIcon from '@mui/icons-material/Star';
import DoneSharpIcon from '@mui/icons-material/DoneSharp';
import { GridRenderCellParams } from '@mui/x-data-grid-premium';
import { useDispatch } from 'react-redux';
import { updateFailureGradeMatrixRanges, updateFailureMatrixGrade } from '@Actions';
import { selectFailureGradeMatrixIsLoading } from '@Selectors';

interface GradeLegendMenuProps {
	anchorEl: HTMLElement | null;
	open: boolean;
	selectedGrade: string;
	handleClose(): void;
	handleGradeChange(newGrade: string, modelId: string): void;
	modelId: string;
}

export const GradeLegendMenu = ({
	anchorEl,
	open,
	handleClose,
	selectedGrade,
	handleGradeChange,
	modelId,
}: GradeLegendMenuProps) => {
	const { t } = useGlobalization();
	const classes = useGradeMatrixStyles();

	return (
		<Menu
			anchorEl={anchorEl}
			id="grade-legend-menu"
			open={open}
			onClose={handleClose}
			onClick={handleClose}
			MenuListProps={{
				'aria-labelledby': 'gridcell-button',
			}}
			PaperProps={{
				elevation: 0,
				sx: {
					overflow: 'visible',
					filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
					minWidth: '183px',
					mt: 1.5,
					'& .Mui-selected': {
						backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
					},
				},
			}}
			transformOrigin={{ horizontal: 'right', vertical: 'top' }}
			anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
			{legends.map((legend, key) => (
				<MenuItem
					key={key}
					value={selectedGrade}
					onClick={() => handleGradeChange(legend.name.toLowerCase(), modelId)}
					selected={selectedGrade.toUpperCase() === legend.name.toUpperCase()}>
					<div className={classes.gridCellMenuItemContainer}>
						<div className={classes.gridCellMenuItemIconContainer}>
							<ListItemIcon>
								<StarIcon
									style={{
										width: '21px',
										color: legend.color,
									}}
								/>
							</ListItemIcon>
						</div>
						<div className={classes.gridCellMenuItemLegendName}>{t(legend.displayName)}</div>
						{selectedGrade.toUpperCase() === legend.name.toUpperCase() ? (
							<div className={classes.gridCellMenuItemDoneIconContainer}>
								<DoneSharpIcon className={classes.gridCellMenuItemDoneIcon} />
							</div>
						) : (
							<></>
						)}
					</div>
				</MenuItem>
			))}
		</Menu>
	);
};

export interface MatrixCell {
	grade: 'extreme' | 'high' | 'medium' | 'low' | 'negligible';
	count: number;
	x: number;
	y: number;
}

export const GradeMatrixCell = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	props: GridRenderCellParams<any, MatrixCell>,
) => {
	const { value, field, row } = props;
	const dispatch = useDispatch();
	const classes = useGradeMatrixStyles();
	const isLoading = selectFailureGradeMatrixIsLoading();

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);

	// Extract coordinates from grid position
	const xCoordinate = field.startsWith('rul-') ? parseInt(field.split('-')[1]) : 0;
	const yCoordinate = parseInt(row.id.split('-')[1]);

	const handleClick = (event: MouseEvent<HTMLDivElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleGradeChange = (newGrade: string, modelId: string) => {
		setAnchorEl(null); // Close menu

		dispatch(
			updateFailureMatrixGrade({
				new_grade: newGrade,
				old_grade: value?.grade || '',
				j: xCoordinate,
				i: yCoordinate,
				type: 'grade',
				modelId: modelId,
			}),
		);
	};

	if (isLoading) {
		return (
			<div id="gridcell-container" className={classes.gridCellContainer}>
				<div className={classes.cellButton}>
					<Skeleton variant="circular" width={16} height={16} sx={{ marginBottom: '2px' }} />
					<Skeleton variant="text" width={20} height={16} sx={{ marginLeft: '4px' }} />
				</div>
			</div>
		);
	}

	return (
		<div id="gridcell-container" className={classes.gridCellContainer}>
			<div
				id="gridcell-button"
				aria-controls={open ? 'risk-legend-menu' : undefined}
				aria-haspopup="true"
				aria-expanded={open ? 'true' : undefined}
				onClick={handleClick}
				onKeyDown={() => {
					return null;
				}}
				role="button"
				tabIndex={0}
				className={classes.cellButton}>
				<StarIcon
					style={{
						width: '16px',
						marginBottom: '2px',
						color: legendsColors[value?.grade as keyof typeof legendsColors],
					}}
				/>
				<span>{value?.count}</span>
			</div>
			<GradeLegendMenu
				anchorEl={anchorEl}
				open={open}
				selectedGrade={value?.grade ?? 'medium'}
				handleClose={handleClose}
				handleGradeChange={handleGradeChange}
				modelId={row.modelId || ''}
			/>
		</div>
	);
};

interface RangeEditorDialogrProps {
	showEditRangesDialog: boolean;
	setShowEditRangesDialog: Dispatch<SetStateAction<boolean>>;
	rulRangeList: number[];
	fpRangeList: number[];
	modelId: string;
}
export const RangeEditorDialog = ({
	showEditRangesDialog,
	setShowEditRangesDialog,
	rulRangeList,
	fpRangeList,
	modelId,
}: RangeEditorDialogrProps) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const [disableSaveBtn, setDisableSaveBtn] = useState(true);
	const [fpError, setFpError] = useState('');
	const [rulError, setRulError] = useState('');
	const isLoading = selectFailureGradeMatrixIsLoading();

	const errorMessage = fpError || rulError;

	return (
		<Dialog open={showEditRangesDialog} onClose={() => setShowEditRangesDialog(false)} maxWidth="md" fullWidth>
			<DialogTitle sx={{ fontWeight: 700, fontSize: '1.25rem', pb: 0.5 }}>{t('Edit Ranges')}</DialogTitle>
			<DialogContent>
				<Typography sx={{ color: 'text.secondary', fontSize: '0.875rem', mb: 3 }}>
					{t(
						'Set the boundaries for the Failure Grade Matrix. Each row defines a Failure Probability bucket, each column a Remaining Useful Life bucket. The combination determines the grade.',
					)}
				</Typography>
				<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
					<RangeEditor
						rangeName="fp"
						rangeList={fpRangeList}
						setDisableSaveBtn={setDisableSaveBtn}
						setErrorMessage={setFpError}
					/>
					<RangeEditor
						rangeName="rul"
						rangeList={rulRangeList}
						setDisableSaveBtn={setDisableSaveBtn}
						setErrorMessage={setRulError}
					/>
				</Box>
				{errorMessage && (
					<Box
						sx={{
							display: 'flex',
							alignItems: 'stretch',
							bgcolor: '#fee2e2',
							borderRadius: 1,
							overflow: 'hidden',
							mt: 3,
						}}>
						<Box
							sx={{
								bgcolor: '#b91c1c',
								px: 1.5,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								flexShrink: 0,
							}}>
							<Box sx={{ position: 'relative', width: 24, height: 24 }}>
								<Box
									sx={{
										width: 18,
										height: 18,
										bgcolor: 'white',
										transform: 'rotate(45deg)',
										position: 'absolute',
										top: 3,
										left: 3,
									}}
								/>
								<Box
									sx={{
										position: 'absolute',
										inset: 0,
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										fontWeight: 900,
										color: '#b91c1c',
										fontSize: '0.8125rem',
										lineHeight: 1,
									}}>
									!
								</Box>
							</Box>
						</Box>
						<Typography sx={{ px: 2, py: 1.5, fontSize: '0.875rem', color: '#1f2937' }}>{t(errorMessage)}</Typography>
					</Box>
				)}
			</DialogContent>
			<DialogActions sx={{ px: 3, py: 2 }}>
				<Button variant="outlined" disabled={isLoading} onClick={() => setShowEditRangesDialog(false)}>
					{t('Cancel')}
				</Button>
				<Button
					variant="contained"
					disabled={disableSaveBtn || isLoading}
					onClick={() => {
						dispatch(
							updateFailureGradeMatrixRanges({
								failure_probabilities: [...fpRangeList],
								ruls: [...rulRangeList],
								modelId: modelId,
								type: 'range',
							}),
						);
						setDisableSaveBtn(false);
						setShowEditRangesDialog(false);
					}}>
					{isLoading ? t('Saving...') : t('Save Changes')}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

interface RangeEditorProps {
	rangeName: string;
	rangeList: number[];
	setDisableSaveBtn: Dispatch<SetStateAction<boolean>>;
	setErrorMessage: (msg: string) => void;
}
const RangeEditor = ({ rangeName, rangeList, setDisableSaveBtn, setErrorMessage }: RangeEditorProps) => {
	const stepValue = 0.01;
	const arr = [...rangeList];
	const { t } = useGlobalization();

	// Determine if this is a descending range (FP) or ascending range (RUL)
	const isDescending = rangeName === 'fp';

	const getMinValue = (val: number) => {
		return (val + 0.01).toFixed(2);
	};

	const getMaxValue = (val: number) => {
		return (val - 0.01).toFixed(2);
	};

	const [val1, setVal1] = useState(arr[0].toString());
	const [val2, setVal2] = useState(arr[1].toString());
	const [val3, setVal3] = useState(arr[2].toString());
	const [val4, setVal4] = useState(arr[3].toString());
	const [val5, setVal5] = useState(arr[4].toString());

	const switchBtn = () => {
		const v = [val1, val2, val3, val4, val5].map(parseFloat);
		const label = isDescending ? 'FP' : 'RUL';

		if (isDescending) {
			if (v[0] <= 0 || v[4] <= 0) {
				setErrorMessage(`First ${label} value must be greater than 0.`);
				setDisableSaveBtn(true);
				return;
			}
			if (!(v[0] > v[1] && v[1] > v[2] && v[2] > v[3] && v[3] > v[4])) {
				setErrorMessage(`${label} values must be in descending order.`);
				setDisableSaveBtn(true);
				return;
			}
		} else {
			if (v[0] <= 0) {
				setErrorMessage(`First ${label} value must be greater than 0.`);
				setDisableSaveBtn(true);
				return;
			}
			if (!(v[0] < v[1] && v[1] < v[2] && v[2] < v[3] && v[3] < v[4])) {
				setErrorMessage(`${label} values must be in ascending order.`);
				setDisableSaveBtn(true);
				return;
			}
		}
		setErrorMessage('');
		setDisableSaveBtn(false);
	};

	useEffect(() => {
		switchBtn();
	}, [val1, val2, val3, val4, val5]);

	useEffect(() => {
		setDisableSaveBtn(true);
	}, []);

	const unit = rangeName === 'fp' ? '%' : t('years');

	const renderRow = (
		val: string,
		setVal: (v: string) => void,
		error: boolean,
		inputProps: Record<string, unknown>,
		rangeIdx: number,
	) => (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
			<Typography sx={{ width: '12px', flexShrink: 0 }}>&le;</Typography>
			<TextField
				size="small"
				type="number"
				fullWidth
				error={error}
				InputProps={{
					...inputProps,
					endAdornment: <InputAdornment position="end">{unit}</InputAdornment>,
				}}
				value={val}
				onChange={e => setVal(e.target.value)}
				onBlur={e => {
					rangeList[rangeIdx] = +e.target.value;
				}}
			/>
		</Box>
	);

	return (
		<Paper variant="outlined" sx={{ p: 3 }}>
			<Typography sx={{ fontWeight: 700, fontSize: '1.125rem', mb: 0.25 }}>
				{rangeName === 'fp' ? t('FP') : t('RUL')}
			</Typography>
			<Typography sx={{ color: 'text.secondary', fontSize: '0.875rem', mb: 2 }}>
				{rangeName === 'fp' ? t('Failure Probability (%)') : t('Remaining Useful Life (Years)')}
			</Typography>
			{renderRow(
				val1,
				setVal1,
				isDescending
					? parseFloat(val1) <= 0 || parseFloat(val1) <= parseFloat(val2)
					: parseFloat(val1) < 0.01 || parseFloat(val1) >= parseFloat(val2),
				{
					inputProps: {
						min: stepValue,
						max: isDescending ? undefined : getMaxValue(parseFloat(val2)),
						step: stepValue,
					},
				},
				0,
			)}
			<Divider />
			{renderRow(
				val2,
				setVal2,
				isDescending
					? parseFloat(val2) <= 0 || parseFloat(val2) >= parseFloat(val1) || parseFloat(val2) <= parseFloat(val3)
					: parseFloat(val2) <= parseFloat(val1) || parseFloat(val2) >= parseFloat(val3),
				{
					inputProps: {
						min: isDescending ? stepValue : getMinValue(parseFloat(val1)),
						max: isDescending ? getMaxValue(parseFloat(val1)) : getMaxValue(parseFloat(val3)),
						step: stepValue,
					},
				},
				1,
			)}
			<Divider />
			{renderRow(
				val3,
				setVal3,
				isDescending
					? parseFloat(val3) <= 0 || parseFloat(val3) >= parseFloat(val2) || parseFloat(val3) <= parseFloat(val4)
					: parseFloat(val3) <= parseFloat(val2) || parseFloat(val3) >= parseFloat(val4),
				{
					inputProps: {
						min: isDescending ? stepValue : getMinValue(parseFloat(val2)),
						max: isDescending ? getMaxValue(parseFloat(val2)) : getMaxValue(parseFloat(val4)),
						step: stepValue,
					},
				},
				2,
			)}
			<Divider />
			{renderRow(
				val4,
				setVal4,
				isDescending
					? parseFloat(val4) <= 0 || parseFloat(val4) >= parseFloat(val3) || parseFloat(val4) <= parseFloat(val5)
					: parseFloat(val4) <= parseFloat(val3) || parseFloat(val4) > parseFloat(val5),
				{
					inputProps: {
						min: isDescending ? stepValue : getMinValue(parseFloat(val3)),
						max: isDescending ? getMaxValue(parseFloat(val3)) : getMaxValue(parseFloat(val5)),
						step: stepValue,
					},
				},
				3,
			)}
			<Divider />
			{renderRow(
				val5,
				setVal5,
				isDescending
					? parseFloat(val5) <= 0 || parseFloat(val5) >= parseFloat(val4)
					: parseFloat(val5) <= parseFloat(val4),
				{
					inputProps: {
						min: isDescending ? stepValue : getMinValue(parseFloat(val4)),
						max: isDescending ? getMaxValue(parseFloat(val4)) : undefined,
						step: stepValue,
					},
				},
				4,
			)}
		</Paper>
	);
};
